import { CTR } from 'aes-js';
import { getBytes, keccak256, toUtf8Bytes } from 'ethers';

const STORAGE_CIPHER_PREFIX = 'wpaes:1:';
const STORAGE_SECRET_SEED = 'winpay-h5-storage-aes-secret';

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const cachedKeyBytesMap = new Map();

function toHex(bytes) {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function fromHex(hex) {
  if (!hex || typeof hex !== 'string' || hex.length % 2 !== 0) {
    return new Uint8Array();
  }

  const bytes = new Uint8Array(hex.length / 2);
  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16);
  }
  return bytes;
}

function getPrimaryStorageSeed() {
  if (typeof window === 'undefined') {
    return STORAGE_SECRET_SEED;
  }

  return [
    STORAGE_SECRET_SEED,
    window.location?.origin || ''
  ].join('|');
}

function getLegacyStorageSeeds() {
  if (typeof window === 'undefined') {
    return [];
  }

  return [[
    STORAGE_SECRET_SEED,
    window.location?.origin || '',
    navigator.userAgent || '',
    navigator.language || ''
  ].join('|')];
}

function getKeyBytes(seed = getPrimaryStorageSeed()) {
  if (cachedKeyBytesMap.has(seed)) {
    return cachedKeyBytesMap.get(seed);
  }

  const keyBytes = getBytes(keccak256(toUtf8Bytes(seed)));
  cachedKeyBytesMap.set(seed, keyBytes);
  return keyBytes;
}

function randomBytes(length = 16) {
  const bytes = new Uint8Array(length);
  window.crypto?.getRandomValues(bytes);
  return bytes;
}

export function encryptStorageValue(value) {
  const plainText = String(value ?? '');
  const iv = randomBytes(16);
  const cipher = new CTR(getKeyBytes(getPrimaryStorageSeed()), iv);
  const encryptedBytes = cipher.encrypt(textEncoder.encode(plainText));
  return `${STORAGE_CIPHER_PREFIX}${toHex(iv)}:${toHex(encryptedBytes)}`;
}

function decryptBySeed(value, seed) {
  if (typeof value !== 'string' || !value.startsWith(STORAGE_CIPHER_PREFIX)) {
    return value ?? null;
  }

  const payload = value.slice(STORAGE_CIPHER_PREFIX.length);
  const [ivHex, dataHex] = payload.split(':');
  if (!ivHex || !dataHex) {
    return null;
  }

  try {
    const cipher = new CTR(getKeyBytes(seed), fromHex(ivHex));
    const decryptedBytes = cipher.decrypt(fromHex(dataHex));
    return textDecoder.decode(decryptedBytes);
  } catch (error) {
    return null;
  }
}

export function decryptStorageValue(value) {
  if (typeof value !== 'string' || !value.startsWith(STORAGE_CIPHER_PREFIX)) {
    return value ?? null;
  }

  const primarySeed = getPrimaryStorageSeed();
  const primaryResult = decryptBySeed(value, primarySeed);
  if (primaryResult !== null) {
    return primaryResult;
  }

  for (const seed of getLegacyStorageSeeds()) {
    const result = decryptBySeed(value, seed);
    if (result !== null) {
      return result;
    }
  }

  return null;
}

export function getStorage(type = 'local') {
  if (typeof window === 'undefined') {
    return null;
  }
  return type === 'session' ? window.sessionStorage : window.localStorage;
}

export function readStorageItem(key, { storage = getStorage('local'), migrate = true } = {}) {
  const raw = storage?.getItem(key);
  if (!raw) {
    return null;
  }

  let decrypted = null;
  let usedLegacySeed = false;

  if (typeof raw === 'string' && raw.startsWith(STORAGE_CIPHER_PREFIX)) {
    const primarySeed = getPrimaryStorageSeed();
    decrypted = decryptBySeed(raw, primarySeed);

    if (decrypted === null) {
      for (const seed of getLegacyStorageSeeds()) {
        decrypted = decryptBySeed(raw, seed);
        if (decrypted !== null) {
          usedLegacySeed = true;
          break;
        }
      }
    }
  } else {
    decrypted = decryptStorageValue(raw);
  }

  if (decrypted === null) {
    return null;
  }

  if (migrate && typeof raw === 'string' && (!raw.startsWith(STORAGE_CIPHER_PREFIX) || usedLegacySeed)) {
    storage?.setItem(key, encryptStorageValue(decrypted));
  }

  return decrypted;
}

export function writeStorageItem(key, value, { storage = getStorage('local') } = {}) {
  if (!storage) {
    return;
  }
  storage.setItem(key, encryptStorageValue(value));
}

export function removeStorageItem(key, { storage = getStorage('local') } = {}) {
  storage?.removeItem(key);
}

export function readStorageJSON(key, fallback, options = {}) {
  try {
    const raw = readStorageItem(key, options);
    return raw ? JSON.parse(raw) : fallback;
  } catch (error) {
    return fallback;
  }
}

export function writeStorageJSON(key, value, options = {}) {
  writeStorageItem(key, JSON.stringify(value), options);
}
